import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import * as React from 'react'
import { useDraggable } from '@dnd-kit/core'
import { useTimelineDragContext } from './TripPlanCanvasDragContext'
import { canDragTimelineItem, type TimelineItem as TimelineItemData } from './tripPlanCanvasReorder'
import { useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import { computeStayDateRange } from 'tripPlanner/utils/chatDates'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Expander from 'components/utils/Expander'
import ItineraryItemCard from './ItineraryItemCard'
import ItineraryContent, { ItinerarySummaryRow, DatePill, getPreviewImages } from './ItineraryContent'
import Divider from 'LuxKit/Divider'
import { groupBy } from 'lib/array/arrayUtils'
import { isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import ChatItemDropdown from './ChatItemDropdown'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import PlaceInfoDrawer from './PlaceInfoDrawer'
import ChatOfferDetailDrawer from './ChatOfferDetailDrawer'
import ChatExperienceDetailDrawer from './ChatExperienceDetailDrawer'
import type { DrawerHotel } from './HotelListingDrawer'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import AddHotelToTripModal from 'tripPlanner/components/AddToTrip/AddHotelToTripModal'
import { STOP_VISIT_ROLE } from 'tripPlanner/api/types'
import type { ManualItineraryDeleteRequest, StopVisitRole, TripItineraryData, TripStop, TripDay, TripItineraryItem, TripItemWithoutDay, ExperienceMapViewData } from 'tripPlanner/api/types'
import moment from 'moment'

const VALID_ROLES = new Set<string>(Object.values(STOP_VISIT_ROLE))

function isStopVisitRole(value: string): value is StopVisitRole {
  return VALID_ROLES.has(value)
}

function toStopVisitRole(value: string | undefined): StopVisitRole | undefined {
  return value !== undefined && isStopVisitRole(value) ? value : undefined
}

const ITEM_CARD_GAP = 12
const ITEM_TIMELINE_GUTTER = 32
const ITEM_TIMELINE_LINE_OFFSET = 10
const ITEM_TIMELINE_DOT_SIZE = 9
const ITEM_TIMELINE_DASH_LENGTH = 3
const ITEM_TIMELINE_DASH_GAP = 5

const TO_BE_PLANNED_KEY = 'to-be-planned' as const
const DROP_TARGET_CLASS_NAME = 'is-drop-target'
// Accordion expansion is keyed on a day's dayNumber, or this sentinel for the
// unscheduled ("saved without a date") row.
type ExpansionKey = number | typeof TO_BE_PLANNED_KEY

const PaddedContainer = styled(Sheet)`
  width: 100%;
`

const SummaryList = styled.div`
  width: 100%;
`

const SummaryDivider = styled(Divider)`
  width: 100%;
`

const DaySection = styled.section`
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.M};
  transition: background 120ms ease, box-shadow 120ms ease;

  &.${DROP_TARGET_CLASS_NAME} {
    background: ${props => props.theme.palette.neutral.surface.lightBackground};
    box-shadow: inset 0 0 0 2px ${props => props.theme.palette.neutral.default.one};
  }
`

const UnplannedSection = styled.div`
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.M};
  transition: background 120ms ease, box-shadow 120ms ease;

  &.${DROP_TARGET_CLASS_NAME} {
    background: ${props => props.theme.palette.neutral.surface.lightBackground};
    box-shadow: inset 0 0 0 2px ${props => props.theme.palette.neutral.default.one};
  }
`

const DayDetail = styled.div`
  padding-top: ${rem(8)};
  padding-bottom: ${rem(ITEM_CARD_GAP)};
`

const timelineRailLine = css`
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    inset-inline-start: ${rem(ITEM_TIMELINE_LINE_OFFSET)};
    width: 1px;
    background-image: repeating-linear-gradient(
      to bottom,
      ${props => props.theme.palette.neutral.surface.lightForeground} 0 ${rem(ITEM_TIMELINE_DASH_LENGTH)},
      transparent ${rem(ITEM_TIMELINE_DASH_LENGTH)} ${rem(ITEM_TIMELINE_DASH_LENGTH + ITEM_TIMELINE_DASH_GAP)}
    );
    pointer-events: none;
  }
`

const DayTimelineGrid = styled.div`
  display: grid;
  grid-template-columns: ${rem(ITEM_TIMELINE_GUTTER)} minmax(0, 1fr);
  width: 100%;
`

const DayTimelineRail = styled.div`
  grid-column: 1;
  grid-row: 1 / -1;
  position: relative;

  ${timelineRailLine}
`

const DayTimelineContent = styled.div`
  grid-column: 2;
  min-width: 0;
`

const TimeSectionsContainer = styled.div<{ $withDayDescription: boolean }>`
  width: 100%;
  margin-top: ${props => (props.$withDayDescription ? rem(12) : rem(0))};
  display: flex;
  flex-direction: column;
  gap: ${rem(ITEM_CARD_GAP)};
`

const TimelineList = styled.div<{ $showRail: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${rem(ITEM_CARD_GAP)};

  ${props => props.$showRail && timelineRailLine}
`

interface TimelineItemStyleProps {
  $showRail: boolean
  $draggable?: boolean
  $isDragging?: boolean
  $dropPlacement?: 'before' | 'after'
}

const TimelineItem = styled.div<TimelineItemStyleProps>`
  position: relative;
  width: 100%;
  padding-inline-start: ${props => (props.$showRail ? rem(ITEM_TIMELINE_GUTTER) : rem(0))};
  cursor: ${props => (props.$draggable ? 'grab' : 'auto')};
  opacity: ${props => (props.$isDragging ? 0.4 : 1)};

  ${props => props.$dropPlacement && css`
    &::before {
      content: '';
      position: absolute;
      inset-inline-start: 0;
      inset-inline-end: 0;
      height: ${rem(3)};
      border-radius: ${props.theme.borderRadius.round};
      background: ${props.theme.palette.neutral.default.one};
      pointer-events: none;
      z-index: 1;
      ${props.$dropPlacement === 'before'
    ? `top: ${rem(-ITEM_CARD_GAP / 2)};`
    : `bottom: ${rem(-ITEM_CARD_GAP / 2)};`}
    }
  `}

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    inset-inline-start: ${props => (props.$showRail
      ? rem(ITEM_TIMELINE_LINE_OFFSET - (ITEM_TIMELINE_DOT_SIZE / 2))
      : rem(ITEM_TIMELINE_LINE_OFFSET - ITEM_TIMELINE_GUTTER - (ITEM_TIMELINE_DOT_SIZE / 2)))};
    width: ${rem(ITEM_TIMELINE_DOT_SIZE)};
    height: ${rem(ITEM_TIMELINE_DOT_SIZE)};
    border-radius: 50%;
    background: ${props => props.theme.palette.neutral.surface.darkForeground};
    box-shadow: 0 0 0 ${rem(2)} ${props => props.theme.palette.neutral.surface.normalBackground};
    transform: translateY(-50%);
    pointer-events: none;
  }
`

interface Props {
  data?: TripItineraryData
  stopId?: number
  onSendMessage?: (message: string) => void
  onDeleteTarget?: (target: ManualItineraryDeleteRequest) => Promise<void> | void
  onShowMap?: (hotel: DrawerHotel) => void
  onShowExperienceOnMap?: (data: ExperienceMapViewData) => void
  onAskAI?: (dayNumber: number, dayTitle: string, date?: string) => void
  onAskAIForItem?: (item: TripItineraryItem | TripItemWithoutDay) => void
  scrollTargetDayId?: number
  scrollTargetItemId?: number
}

interface SelectedPlace {
  googlePlaceId: string
  placeName: string
  itemId: number
}

type TimePeriod = 'morning' | 'afternoon' | 'evening'

function getTimePeriod(timeString?: string): TimePeriod {
  if (!timeString) {
    return 'morning'
  }
  const hours = moment(timeString, 'HH:mm').hours()
  if (hours < 12) {
    return 'morning'
  }
  if (hours < 18) {
    return 'afternoon'
  }
  return 'evening'
}

// --- Render helpers ---

interface ItemCardListProps {
  items: Array<TripItineraryItem | TripItemWithoutDay>
  dayDate?: string
  showRail?: boolean
  onDelete: (itemId: number) => void
  onClick?: (googlePlaceId: string, placeName: string, itemId: number) => void
  onViewOfferDetails: (item: TripItineraryItem | TripItemWithoutDay) => void
  onAddToTrip?: (item: TripItineraryItem | TripItemWithoutDay) => void
  onAskAI?: (item: TripItineraryItem | TripItemWithoutDay) => void
}

function getStayDateRange(item: TripItineraryItem | TripItemWithoutDay, dayDate?: string): string | undefined {
  if (!dayDate) return undefined
  const dayOfStay = 'dayOfStay' in item ? item.dayOfStay : undefined
  const totalDays = 'totalDays' in item ? item.totalDays : undefined
  if (!dayOfStay || !totalDays) return undefined
  return computeStayDateRange(dayDate, dayOfStay, totalDays)
}

interface DraggableTimelineItemProps {
  item: TimelineItemData
  showRail: boolean
  children: ReactNode
}

function DraggableTimelineItem({ item, showRail, children }: DraggableTimelineItemProps) {
  const { dragEnabled, activeItemId, overItemId, dropPlacement } = useTimelineDragContext()
  const canDrag = dragEnabled && canDragTimelineItem(item)
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: item.id, disabled: !canDrag })

  return (
    <TimelineItem
      ref={setNodeRef}
      data-item-id={item.id}
      data-timeline-item-id={item.id}
      $showRail={showRail}
      $draggable={canDrag}
      $isDragging={isDragging || activeItemId === item.id}
      $dropPlacement={overItemId === item.id ? dropPlacement : undefined}
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
    >
      {children}
    </TimelineItem>
  )
}

function ItemCardList({ items, dayDate, showRail = true, onDelete, onClick, onViewOfferDetails, onAddToTrip, onAskAI }: ItemCardListProps) {
  return (
    <TimelineList $showRail={showRail}>
      {items.map((item) => (
        <DraggableTimelineItem key={item.id} item={item} showRail={showRail}>
          <ItineraryItemCard
            item={item}
            role={toStopVisitRole('role' in item ? item.role : undefined)}
            isCheckIn={'isCheckIn' in item ? item.isCheckIn : undefined}
            dayOfStay={'dayOfStay' in item ? item.dayOfStay : undefined}
            totalDays={'totalDays' in item ? item.totalDays : undefined}
            stayDateRange={getStayDateRange(item, dayDate)}
            onDelete={onDelete}
            onClick={onClick}
            onViewOfferDetails={onViewOfferDetails}
            onAddToTrip={onAddToTrip}
            onAskAI={onAskAI}
          />
        </DraggableTimelineItem>
      ))}
    </TimelineList>
  )
}

interface DayDetailContentProps {
  day: TripDay
  items: Array<TripItineraryItem>
  onDeleteItem: (itemId: number) => void
  onItemClick: (googlePlaceId: string, placeName: string, itemId: number) => void
  onViewOfferDetails: (item: TripItineraryItem | TripItemWithoutDay) => void
  onAddToTrip: (item: TripItineraryItem | TripItemWithoutDay) => void
  onAskAI?: (item: TripItineraryItem | TripItemWithoutDay) => void
}

function DayDetailContent({
  day,
  items,
  onDeleteItem,
  onItemClick,
  onViewOfferDetails,
  onAddToTrip,
  onAskAI,
}: DayDetailContentProps) {
  const intl = useIntl()

  // Categorise items for position logic:
  // - ongoingStayItems: condensed cards above time sections
  // - checkInAccommodationItems: placed in time section based on check-in time
  // - regularItems: activities grouped by time period
  const { ongoingStayItems, checkInAccommodationItems, regularItems } = useMemo(() => {
    const ongoing: Array<TripItineraryItem> = []
    const checkInAccom: Array<TripItineraryItem> = []
    const regular: Array<TripItineraryItem> = []
    for (const item of items) {
      const isAccommodation = item.type === 'ACCOMMODATION'
      const role = toStopVisitRole(item.role)
      if (isAccommodation && role === STOP_VISIT_ROLE.STAY && item.isCheckIn === false) {
        ongoing.push(item)
      } else if (isAccommodation) {
        checkInAccom.push(item)
      } else {
        regular.push(item)
      }
    }
    return { ongoingStayItems: ongoing, checkInAccommodationItems: checkInAccom, regularItems: regular }
  }, [items])

  const hasAnyItemsWithTime = useMemo(
    () => regularItems.some(item => Boolean(item.time)) || checkInAccommodationItems.length > 0,
    [regularItems, checkInAccommodationItems],
  )

  const groupedItemsByTime = useMemo(() => {
    const itemsWithTime = regularItems.filter((item): item is TripItineraryItem => Boolean(item.time))
    const grouped = groupBy<TimePeriod, TripItineraryItem>(itemsWithTime, (item) => getTimePeriod(item.time))

    return {
      morning: grouped.get('morning') ?? [],
      afternoon: grouped.get('afternoon') ?? [],
      evening: grouped.get('evening') ?? [],
    }
  }, [regularItems])

  // Activities without a time slot, rendered after time groups
  const itemsWithoutTime = useMemo(() => {
    return regularItems.filter(item => !item.time)
  }, [regularItems])

  const checkInByTimePeriod = useMemo(() => {
    const morning: Array<TripItineraryItem> = []
    const afternoon: Array<TripItineraryItem> = []
    const evening: Array<TripItineraryItem> = []
    for (const item of checkInAccommodationItems) {
      const period = item.time ? getTimePeriod(item.time) : 'afternoon'
      if (period === 'afternoon') {
        afternoon.push(item)
      } else if (period === 'evening') {
        evening.push(item)
      } else {
        morning.push(item)
      }
    }
    return { morning, afternoon, evening }
  }, [checkInAccommodationItems])

  const morningItems = useMemo(
    () => [...checkInByTimePeriod.morning, ...groupedItemsByTime.morning],
    [checkInByTimePeriod.morning, groupedItemsByTime.morning],
  )

  const afternoonItems = useMemo(
    () => [...checkInByTimePeriod.afternoon, ...groupedItemsByTime.afternoon],
    [checkInByTimePeriod.afternoon, groupedItemsByTime.afternoon],
  )

  const eveningItems = useMemo(
    () => [...checkInByTimePeriod.evening, ...groupedItemsByTime.evening],
    [checkInByTimePeriod.evening, groupedItemsByTime.evening],
  )

  const morningHeading = intl.formatMessage({
    id: 'z4paiuuzgrhgzMhtoZcw',
    defaultMessage: 'Morning',
    description: 'Heading for morning time period in the day itinerary',
  })

  const afternoonHeading = intl.formatMessage({
    id: 'ZV9SYhHEFDKtGU36lYXP',
    defaultMessage: 'Afternoon',
    description: 'Heading for afternoon time period in the day itinerary',
  })

  const eveningHeading = intl.formatMessage({
    id: 'EJbXVrC5Lqwu4tHrpv-S',
    defaultMessage: 'Evening',
    description: 'Heading for evening time period in the day itinerary',
  })

  const emptyDayMessage = intl.formatMessage({
    id: 'gSkaW0y1lUiW_IYVyftV',
    defaultMessage: 'No items planned for this day yet.',
    description: 'Empty state message when a trip day has no items planned',
  })

  return (
    <DayDetail>
      <DayTimelineGrid>
        <DayTimelineRail />
        <DayTimelineContent>
          <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
            <Heading variant="heading4">{day.title}</Heading>
            {day.description && (
              <BodyText variant="medium">{day.description}</BodyText>
            )}
            {items.length === 0 && (
              <BodyText variant="medium" colour="light-foreground-alt">
                {emptyDayMessage}
              </BodyText>
            )}
            {ongoingStayItems.length > 0 && (
              <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                <ItemCardList
                  items={ongoingStayItems}
                  dayDate={day.date}
                  showRail={false}
                  onDelete={onDeleteItem}
                  onViewOfferDetails={onViewOfferDetails}
                  onAddToTrip={onAddToTrip}
                  onAskAI={onAskAI}
                />
              </Group>
            )}
            {hasAnyItemsWithTime && (
              <TimeSectionsContainer $withDayDescription={Boolean(day.description)}>
                {morningItems.length > 0 && (
                  <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                    <BodyText variant="large" weight="bold">{morningHeading}</BodyText>
                    <ItemCardList
                      items={morningItems}
                      dayDate={day.date}
                      showRail={false}
                      onDelete={onDeleteItem}
                      onClick={onItemClick}
                      onViewOfferDetails={onViewOfferDetails}
                      onAddToTrip={onAddToTrip}
                      onAskAI={onAskAI}
                    />
                  </Group>
                )}
                {afternoonItems.length > 0 && (
                  <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                    <BodyText variant="large" weight="bold">{afternoonHeading}</BodyText>
                    <ItemCardList
                      items={afternoonItems}
                      dayDate={day.date}
                      showRail={false}
                      onDelete={onDeleteItem}
                      onClick={onItemClick}
                      onViewOfferDetails={onViewOfferDetails}
                      onAddToTrip={onAddToTrip}
                      onAskAI={onAskAI}
                    />
                  </Group>
                )}
                {eveningItems.length > 0 && (
                  <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                    <BodyText variant="large" weight="bold">{eveningHeading}</BodyText>
                    <ItemCardList
                      items={eveningItems}
                      dayDate={day.date}
                      showRail={false}
                      onDelete={onDeleteItem}
                      onClick={onItemClick}
                      onViewOfferDetails={onViewOfferDetails}
                      onAddToTrip={onAddToTrip}
                      onAskAI={onAskAI}
                    />
                  </Group>
                )}
                {itemsWithoutTime.length > 0 && (
                  <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                    <ItemCardList
                      items={itemsWithoutTime}
                      dayDate={day.date}
                      showRail={false}
                      onDelete={onDeleteItem}
                      onClick={onItemClick}
                      onViewOfferDetails={onViewOfferDetails}
                      onAddToTrip={onAddToTrip}
                      onAskAI={onAskAI}
                    />
                  </Group>
                )}
              </TimeSectionsContainer>
            )}
            {!hasAnyItemsWithTime && (regularItems.length > 0 || checkInAccommodationItems.length > 0) && (
              <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                <ItemCardList
                  items={[...checkInAccommodationItems, ...regularItems]}
                  dayDate={day.date}
                  showRail={false}
                  onDelete={onDeleteItem}
                  onClick={onItemClick}
                  onViewOfferDetails={onViewOfferDetails}
                  onAddToTrip={onAddToTrip}
                  onAskAI={onAskAI}
                />
              </Group>
            )}
          </Group>
        </DayTimelineContent>
      </DayTimelineGrid>
    </DayDetail>
  )
}

const HOVER_EXPAND_DELAY_MS = 450

function DayItineraryCanvas(props: Props) {
  const intl = useIntl()
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { overDayId, overUnplannedStopId } = useTimelineDragContext()
  const { isManualItineraryDeletePending } = useItineraryContext()
  const deleteActionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending
  const { data, stopId, onSendMessage, onDeleteTarget, onShowMap, onShowExperienceOnMap, onAskAI, onAskAIForItem, scrollTargetDayId, scrollTargetItemId } = props

  const containerRef = useRef<HTMLDivElement>(null)

  const [selectedPlace, setSelectedPlace] = useState<SelectedPlace | undefined>(undefined)
  const [selectedOffer, setSelectedOffer] = useState<{
    offerId: string
    accommodationPropertyId?: string
    offerType?: string
    searchParams?: OfferClickSearchParams
  } | undefined>(undefined)

  const handleItemClick = useCallback((googlePlaceId: string, placeName: string, itemId: number) => {
    setSelectedPlace({ googlePlaceId, placeName, itemId })
  }, [])

  const handleDrawerClose = useCallback(() => {
    setSelectedPlace(undefined)
  }, [])

  const handleViewOfferDetails = useCallback((item: TripItineraryItem | TripItemWithoutDay) => {
    const savedData = item.savedItemData
    if (savedData?.source === 'offer_experience') {
      setSelectedOffer({
        offerId: savedData.experienceId,
        offerType: 'experience',
      })
      return
    }
    const offerData = isHotelSavedData(savedData) ? savedData : undefined
    if (!offerData) return
    const searchParams: OfferClickSearchParams = {}
    const accommodationPropertyId = (
      'accommodationPropertyId' in offerData && typeof offerData.accommodationPropertyId === 'string'
    ) ? offerData.accommodationPropertyId : undefined
    if (offerData.checkIn) searchParams.checkIn = offerData.checkIn
    if (offerData.checkOut) searchParams.checkOut = offerData.checkOut
    setSelectedOffer({
      offerId: offerData.offerId,
      accommodationPropertyId,
      offerType: offerData.offerType,
      searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
    })
  }, [])

  const handleCloseHotelDrawer = useCallback(() => {
    setSelectedOffer(undefined)
  }, [])

  const handleShowExperienceOnMap = useCallback((experience: { offerId: string; name: string; geoLocation?: { lat: number; lng: number } }) => {
    setSelectedOffer(undefined)
    if (!experience.geoLocation) return
    onShowExperienceOnMap?.({
      searchQuery: experience.name,
      offers: [{ id: experience.offerId, name: experience.name, coordinates: experience.geoLocation }],
    })
  }, [onShowExperienceOnMap])

  const [addToTripItem, setAddToTripItem] = useState<{ name: string } | undefined>(undefined)

  const handleAddToTrip = useCallback((item: TripItineraryItem | TripItemWithoutDay) => {
    setAddToTripItem({ name: item.name })
  }, [])

  const handleAddToTripModalClose = useCallback(() => {
    setAddToTripItem(undefined)
  }, [])

  const handleAddToTripSubmit = useCallback((message: string) => {
    onSendMessage?.(message)
    setAddToTripItem(undefined)
  }, [onSendMessage])

  const stop = useMemo(() => {
    if (!stopId || !data) {
      return undefined
    }
    return data.stops.find((s: TripStop) => s.id === stopId)
  }, [stopId, data])

  const daysForStop = useMemo((): Array<TripDay> => {
    return stop?.days ?? []
  }, [stop])

  const itemsWithoutDay = useMemo(() => {
    return stop?.itemsWithoutDay ?? []
  }, [stop])

  const hasItemsWithoutDay = itemsWithoutDay.length > 0
  const hasDays = daysForStop.length > 0

  const getItemsForDay = useCallback((dayId: number) => {
    const day = daysForStop.find(d => d.id === dayId)
    return (day?.items ?? []).filter(item =>
      !(item.type === 'FLIGHT' && item.savedItemData?.source === 'chat_flight'),
    )
  }, [daysForStop])

  const allDays = useMemo(() =>
    daysForStop
      .map(day => ({ day, items: getItemsForDay(day.id) })),
  [daysForStop, getItemsForDay],
  )

  const toBePlannedPreviewImageIds = useMemo(() => getPreviewImages(itemsWithoutDay), [itemsWithoutDay])

  const toBePlannedExtraImageCount = useMemo(() => (
    Math.max(itemsWithoutDay.length - toBePlannedPreviewImageIds.length, 0)
  ), [itemsWithoutDay.length, toBePlannedPreviewImageIds.length])

  // Accordion expansion state. Empty = everything collapsed (default).
  const [expandedKeys, setExpandedKeys] = useState<Set<ExpansionKey>>(() => new Set())

  const allKeys = useMemo((): Array<ExpansionKey> => {
    return [...daysForStop.map(day => day.dayNumber), TO_BE_PLANNED_KEY]
  }, [daysForStop])

  const allExpanded = allKeys.length > 0 && allKeys.every(key => expandedKeys.has(key))

  const toggleKey = useCallback((key: ExpansionKey) => {
    setExpandedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const toggleUnscheduled = useCallback(() => {
    toggleKey(TO_BE_PLANNED_KEY)
  }, [toggleKey])

  const toggleAll = useCallback(() => {
    setExpandedKeys(prev => {
      const isAll = allKeys.length > 0 && allKeys.every(key => prev.has(key))
      return isAll ? new Set<ExpansionKey>() : new Set<ExpansionKey>(allKeys)
    })
  }, [allKeys])

  // Collapse everything when switching to a different stop.
  useEffect(() => {
    setExpandedKeys(new Set())
  }, [stopId])

  // Hovering a collapsed day while dragging expands it after a short dwell, so an
  // item can be dropped into a day that isn't currently open. `expandedKeys` is read
  // inside the state updater rather than depended on, so an unrelated expand/collapse
  // mid-drag doesn't restart the dwell timer for the hovered day.
  useEffect(() => {
    if (overDayId === undefined) return
    const targetDay = daysForStop.find((day: TripDay) => day.id === overDayId)
    if (!targetDay) return

    const timeout = window.setTimeout(() => {
      setExpandedKeys(prev => prev.has(targetDay.dayNumber) ? prev : new Set(prev).add(targetDay.dayNumber))
    }, HOVER_EXPAND_DELAY_MS)
    return () => window.clearTimeout(timeout)
  }, [overDayId, daysForStop])

  useEffect(() => {
    if (overUnplannedStopId !== stopId) return

    const timeout = window.setTimeout(() => {
      setExpandedKeys(prev => prev.has(TO_BE_PLANNED_KEY) ? prev : new Set(prev).add(TO_BE_PLANNED_KEY))
    }, HOVER_EXPAND_DELAY_MS)
    return () => window.clearTimeout(timeout)
  }, [overUnplannedStopId, stopId])

  // Stream events / external navigation can target a day or an unscheduled item;
  // expand the relevant row so the target is visible.
  useEffect(() => {
    const targetItemIsUnscheduled = scrollTargetItemId !== undefined &&
      itemsWithoutDay.some(item => item.id === scrollTargetItemId)

    if (targetItemIsUnscheduled) {
      setExpandedKeys(prev => new Set(prev).add(TO_BE_PLANNED_KEY))
      return
    }

    if (scrollTargetDayId !== undefined) {
      // Stream events carry a dayId; expansion is keyed on dayNumber.
      const targetDay = daysForStop.find((day: TripDay) => day.id === scrollTargetDayId)
      if (targetDay) {
        setExpandedKeys(prev => new Set(prev).add(targetDay.dayNumber))
      }
    }
  }, [scrollTargetDayId, scrollTargetItemId, daysForStop, itemsWithoutDay])

  const handleDeleteItem = useCallback((itemId: number) => {
    if (deleteActionsDisabled) return
    if (!stop) return
    onDeleteTarget?.({ targetType: 'item', itemId })
  }, [onDeleteTarget, stop, deleteActionsDisabled])

  const handleDeleteDay = useCallback((dayId: number) => {
    if (deleteActionsDisabled) return
    if (!stop) return
    onDeleteTarget?.({ targetType: 'day', dayId })
  }, [onDeleteTarget, stop, deleteActionsDisabled])

  const itineraryHeading = intl.formatMessage({
    id: 'r_qWdTSi7W-DoRG_58QA',
    defaultMessage: 'Itinerary',
    description: 'Heading for the trip itinerary in the trip planner AI chat',
  })

  const expandAllLabel = intl.formatMessage({
    id: '_VIYlAqL6zCJbw4HaJ1j',
    defaultMessage: 'Expand all days',
    description: 'Button label to expand all collapsible day rows in the trip itinerary',
  })

  const collapseAllLabel = intl.formatMessage({
    id: '85L9EAPABzRg0k1OW4rm',
    defaultMessage: 'Collapse all days',
    description: 'Button label to collapse all expanded day rows in the trip itinerary',
  })

  const unscheduledLabel = intl.formatMessage({
    id: 'gkTqlIHJ77WoY566Tk-V',
    defaultMessage: 'Unscheduled',
    description: 'Date pill label for the row holding items not yet assigned to a day in the trip itinerary',
  })

  const savedWithoutDateLabel = intl.formatMessage({
    id: 'Y58X7baZbYEtfKrdtmzK',
    defaultMessage: 'Items saved without a date',
    description: 'Title for the row holding items not yet assigned to a day in the trip itinerary',
  })

  const emptyUnscheduledMessage = intl.formatMessage({
    id: 'EWeF9hWef2lcISi4R7pR',
    defaultMessage: 'No items to plan yet. Add items to your trip to see them here.',
    description: 'Empty state message when there are no unscheduled items in the trip itinerary',
  })

  return (
    <>
      <PaddedContainer ref={containerRef}>
        <Group direction="vertical" gap={16} fullWidth>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" fullWidth>
            <Heading variant="heading6">{itineraryHeading}</Heading>
            {allKeys.length > 0 && (
              <TextButton kind="tertiary" size="small" onClick={toggleAll}>
                {allExpanded ? collapseAllLabel : expandAllLabel}
              </TextButton>
            )}
          </Group>

          {(hasDays || hasItemsWithoutDay) && (
            <SummaryList>
              {allDays.map(({ day, items }) => {
                const expanded = expandedKeys.has(day.dayNumber)
                const dayMenu = expanded ? (
                  <ChatItemDropdown
                    onDelete={() => handleDeleteDay(day.id)}
                    onAskAI={onAskAI ? () => onAskAI(day.dayNumber, day.title, day.date) : undefined}
                  />
                ) : undefined
                return (
                  <DaySection
                    key={day.id}
                    className={overDayId === day.id ? DROP_TARGET_CLASS_NAME : undefined}
                    data-day-id={day.id}
                    data-itinerary-day-section="true"
                  >
                    <ItineraryContent
                      day={day}
                      items={items}
                      expanded={expanded}
                      onToggle={toggleKey}
                      secondaryAction={dayMenu}
                    />
                    <Expander expanded={expanded}>
                      <DayDetailContent
                        day={day}
                        items={items}
                        onDeleteItem={handleDeleteItem}
                        onItemClick={handleItemClick}
                        onViewOfferDetails={handleViewOfferDetails}
                        onAddToTrip={handleAddToTrip}
                        onAskAI={onAskAIForItem}
                      />
                    </Expander>
                    <SummaryDivider kind="secondary" />
                  </DaySection>
                )
              })}
              <UnplannedSection
                className={overUnplannedStopId === stopId ? DROP_TARGET_CLASS_NAME : undefined}
                data-itinerary-unplanned-section="true"
                data-stop-id={stopId}
              >
                <ItinerarySummaryRow
                  indicator={<DatePill>{unscheduledLabel}</DatePill>}
                  title={savedWithoutDateLabel}
                  subtitle={itemsWithoutDay.length === 0 ?
                      intl.formatMessage({
                        id: 'tFNmJsuCyKliFAIMGK8X',
                        defaultMessage: 'Nothing planned yet',
                        description: 'Empty item count label shown in the condensed unscheduled itinerary summary row',
                      }) :
                      intl.formatMessage(
                        {
                          id: 'jCDYdA49dl4m3imZfbgN',
                          defaultMessage: '{count, plural, one {# item} other {# items}}',
                          description: 'Item count label shown in the condensed unscheduled itinerary summary row',
                        },
                        { count: itemsWithoutDay.length },
                      )}
                  previewImageIds={toBePlannedPreviewImageIds}
                  extraImageCount={toBePlannedExtraImageCount}
                  expanded={expandedKeys.has(TO_BE_PLANNED_KEY)}
                  onClick={toggleUnscheduled}
                />
                <Expander expanded={expandedKeys.has(TO_BE_PLANNED_KEY)}>
                  <DayDetail>
                    <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                      {hasItemsWithoutDay ? (
                        <ItemCardList
                          items={itemsWithoutDay}
                          onDelete={handleDeleteItem}
                          onClick={handleItemClick}
                          onViewOfferDetails={handleViewOfferDetails}
                          onAddToTrip={handleAddToTrip}
                          onAskAI={onAskAIForItem}
                        />
                      ) : (
                        <BodyText variant="medium" colour="light-foreground-alt">{emptyUnscheduledMessage}</BodyText>
                      )}
                    </Group>
                  </DayDetail>
                </Expander>
              </UnplannedSection>
            </SummaryList>
          )}
        </Group>
      </PaddedContainer>

      {selectedPlace && (
        <PlaceInfoDrawer
          isOpen
          onClose={handleDrawerClose}
          googlePlaceId={selectedPlace.googlePlaceId}
          placeName={selectedPlace.placeName}
          itemId={selectedPlace.itemId}
          onDelete={handleDeleteItem}
          itineraryData={data}
          onSendMessage={onSendMessage}
        />
      )}

      {selectedOffer?.offerType === 'experience' && (
        <ChatExperienceDetailDrawer
          offerId={selectedOffer.offerId}
          isOpen
          onClose={handleCloseHotelDrawer}
          onShowMap={handleShowExperienceOnMap}
          itineraryData={data}
          onSendMessage={onSendMessage}
        />
      )}
      {selectedOffer && selectedOffer.offerType !== 'experience' && (
        <ChatOfferDetailDrawer
          offerId={selectedOffer.offerId}
          accommodationPropertyId={selectedOffer.accommodationPropertyId}
          offerType={selectedOffer.offerType}
          isOpen
          onClose={handleCloseHotelDrawer}
          onShowMap={onShowMap}
          itineraryData={data}
          onSendMessage={onSendMessage}
          searchParams={selectedOffer.searchParams}
        />
      )}

      {addToTripItem && data && (
        <AddHotelToTripModal
          isOpen
          hotelName={addToTripItem.name}
          itineraryData={data}
          relevantStop={stop}
          onClose={handleAddToTripModalClose}
          onSubmit={handleAddToTripSubmit}
        />
      )}
    </>
  )
}

export default DayItineraryCanvas
